import React, { useRef } from 'react';
import { useState, useEffect, usePrintContext, Hooks, Form, Field, Title, Code, Actions, API, Utility, Button, Messages, Control } from 'helpers/PrintImports';
import { Client } from 'models/ClientModel';
import SignaturePad from "components/common/SignManager";
import { useSearchParams } from 'react-router-dom';

const ConsentPrint11: React.FC = () => {
  const titleName = 'Patient Consent Form (P.O.F.)';
  const [formId, formName] = [Code.Form.Id.CLIENT, Code.Form.Name.CLIENT];
  const {keyId, loading, setLoading, showMenu, setShowMenu, message, setMessage, error, setError, menuPosition, setMenuPosition, contextMenuRef } = usePrintContext();
  const [clientData, setClientData] = useState<Client>({});
  const [signData, setSignData] = useState<string>('');
  const [receiverName, setReceiverName] = useState<string>('');
  const [receiverPhone, setReceiverPhone] = useState<string>('');

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const today = Utility.Date.Current();

  const Privilege = Hooks.Privilege.List(formId);
  const printRef = useRef<HTMLDivElement>(null);
  Hooks.Privilege.HasPrint(Privilege);
  const companyName = Utility.Storage.CompanyName();
  const companyId = Utility.Storage.CompanyId();
  const locationId = Utility.Storage.LocationId();
  

  useEffect(() => {
    if(!clientId) return;
    API.Data.GetByKeyId<Client>(formName, clientId, setLoading, setError)
      .then(async(data) => { if(data) {
      const formData = await Form.Map.Data(data);
      setClientData((prevData) => ({
        ...prevData,
        ...formData,
      }));
      setReceiverName(Utility.Get.FullName(data,Code.Form.Name.CLIENT))
      setReceiverPhone(data.client_phone || '')
    }})
    setLoading(false);
  }, []); 

  return (
    <Control.Print.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      {!loading && (<>
        <Messages.Toast Messages={message} Error={error} />
        <Button.Print keyId={keyId} formName={'FORM'} Privilege={Privilege} clientData={clientData} fileName={titleName} docType={Code.Option.DocumentType.CONSENT_FORM} printRef={printRef} setMessage={setMessage} setError={setError} />
        <Control.Print.Data ref={printRef}>
            <Control.Flex>
              <Control.Column size={[0,60,0,0]}>
                <Control.Box>
                  <Field.CompanyHead companyId={companyId} locationId={locationId} />
                </Control.Box>
                <Control.Box>
                  <Field.Value value={Utility.Get.FullName(clientData, Code.Form.Name.CLIENT).toUpperCase()} valueBold={true} />
                  <Field.Value value={Utility.Format.Phone(clientData?.client_phone)} />
                  <Field.Value value={`${clientData?.client_address} ${clientData?.client_postal}`} />
                  <Field.Value title={Title.DOB} value={Utility.Format.DateOnly(clientData?.client_dob)} />
                  <Field.Value title={Title.ClientId} value={`#${clientData.client_number}`} />
                </Control.Box>
              </Control.Column>
              <Control.Column size={[0,40,0,0]} align={Code.Align.RIGHT}>
                <Control.Box>
                  <Field.SubHead title={titleName} />
                </Control.Box>
              </Control.Column>
            </Control.Flex>
            <div className="content-consent pt8">
              <p><strong>PATIENT CONSENT FORM (P.O.F.)</strong></p>
              <p className='tjust'>I authorize the clinic to fit and install new lenses into my own frame (Patient Own Frame / P.O.F.). I understand that my frame may have existing wear, stress, or damage that may not be visible before lens installation.</p>
              <p className='tjust'>I acknowledge and accept that the clinic is not responsible for any breakage, cracking, bending, scratching, or other damage to my frame during the lens fitting, adjustment, or normal dispensing process. No warranty is provided on patient-owned frames.</p>
              <p className='tjust'>I understand that due to the condition, age, shape, or limitations of my frame, lens fitment may not be perfect. Minor cosmetic gaps, looseness, tightness, alignment differences, or fitting limitations may occur and are accepted by me.</p>
              <p className='tjust'>I understand and agree that all P.O.F. lens orders are final sale. No redo, remake, exchange, refund, or warranty will be provided for lenses ordered into my own frame unless required due to manufacturer defect in the lenses.</p>
              <p className='tjust'>I consent to the collection, use, and electronic storage of my personal and health information, including contact details, prescription information, examination records, and signatures within the clinic’s EHR/EMR system for the purpose of providing eye care services, maintaining records, and ensuring continuity of care. I understand that I may withdraw my consent at any time by submitting a written request; however, doing so may limit {companyName} ability to provide certain services.</p>
              <p className='tjust'>I consent to being contacted by phone, SMS/text message, email, or other electronic communication regarding appointments, order updates, recalls, follow-ups, billing, and other clinic-related communications. The clinic will take reasonable measures to safeguard my personal information in accordance with applicable privacy laws; however, absolute security cannot be guaranteed.</p>
              <p className='tjust'>My information may be shared with authorized third-party applications, healthcare providers, and service partners involved in delivering my care, or disclosed as required by applicable law. Any legal matters arising from this consent shall fall under the jurisdiction of Calgary, Alberta, Canada. I agree that under no circumstances any legal claim can be more than the amount charged to me for the rendered services by {companyName}.</p>
              <p className='tjust'>The full Privacy Policy is available on the official {companyName} website.</p>
              <p className='tjust'>Declaration:</p>
              <p className='tjust'>I confirm that I have read and understood this consent form and voluntarily agree to the terms listed above.</p>
              <p><strong>Date:</strong> {Utility.Format.Date(today,"MMMMM dd, yyyy")}</p>
            </div>
            <SignaturePad signData={signData} setSignData={setSignData} receiverName={receiverName} setReceiverName={setReceiverName} receiverPhone={receiverPhone} setReceiverPhone={setReceiverPhone} />
            
            <Field.PrintedOn fixed={false} />
        </Control.Print.Data>    
      </>)}
      {showMenu && (<div ref={contextMenuRef}><Control.PrintContextMenu keyId={keyId} showMenu={showMenu} formName={formName}  setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Print />
    </Control.Print.Frame>
  );
};

export default ConsentPrint11;